// ProtectWorkSection.jsx (CSS Animations Version)
import { useEffect, useRef } from "react";

const ShieldIcon = ({ className = "w-7 h-7 text-white" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const LockIcon = () => (
  <svg
    className="w-[22px] h-[22px] text-[#1A3F5C]"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = () => (
  <svg
    className="w-[22px] h-[22px] text-[#2563EB]"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    className="w-[22px] h-[22px] text-[#2D7A4F]"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const cards = [
  {
    icon: <LockIcon />,
    bg: "linear-gradient(135deg,#e8ecf0 0%,#dde3ea 100%)",
    titleColor: "text-[#1A3F5C]",
    title: "Smart access control",
    desc: "Team members can only see and do what they're supposed to.",
  },
  {
    icon: <ShieldIcon className="w-[22px] h-[22px] text-[#4A4A9E]" />,
    bg: "linear-gradient(135deg,#ebe8f5 0%,#e0dbf0 100%)",
    titleColor: "text-[#0D1B2A]",
    title: "Protected storage",
    desc: "Your data is locked up tight, whether it's sitting in storage or being sent.",
  },
  {
    icon: <EyeIcon />,
    bg: "linear-gradient(135deg,#e4eaf7 0%,#d8e2f5 100%)",
    titleColor: "text-[#0D1B2A]",
    title: "Privacy matters",
    desc: "We collect only what's needed and keep clear lines about who owns what.",
  },
  {
    icon: <RefreshIcon />,
    bg: "linear-gradient(135deg,#e4f0e8 0%,#d8ebe0 100%)",
    titleColor: "text-[#0D1B2A]",
    title: "Always reliable",
    desc: "Automatic backups and systems designed to stay up and running.",
  },
];

export default function ProtectWorkSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 },
    );

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="protect-work-section min-h-screen flex flex-col items-center justify-center px-6 py-16 overflow-hidden"
      style={{ background: "linear-gradient(180deg,#f0f3f7 0%,#f7f9fb 100%)" }}
    >
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .animate-on-scroll {
          opacity: 0;
        }

        .animate-on-scroll.animate-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .shield-icon {
          animation: pulse 2s infinite;
        }

        .card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid transparent; /* Ya 0px */
          background-clip: padding-box;
        }

        .card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          border-color: transparent;
        }

        .card-icon {
          transition: all 0.3s ease;
        }

        .card:hover .card-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .card-title {
          transition: transform 0.2s ease;
        }

        .card:hover .card-title {
          transform: translateX(4px);
        }

        .card-underline {
          width: 0;
          height: 2px;
          background: rgba(9, 58, 91, 0.2);
          transition: width 0.3s ease;
          margin-top: 8px;
        }

        .card:hover .card-underline {
          width: 100%;
        }

        .float-bg {
          position: absolute;
          border-radius: 50%;
          background: rgba(9, 58, 91, 0.05);
          animation: float 6s ease-in-out infinite;
          pointer-events: none;
        }

        .delay-1 {
          animation-delay: 0s;
        }
        .delay-2 {
          animation-delay: 1s;
        }
        .delay-3 {
          animation-delay: 2s;
        }
        .delay-4 {
          animation-delay: 3s;
        }
        .delay-5 {
          animation-delay: 4s;
        }

        .stagger-1 {
          animation-delay: 0.1s;
        }
        .stagger-2 {
          animation-delay: 0.2s;
        }
        .stagger-3 {
          animation-delay: 0.3s;
        }
        .stagger-4 {
          animation-delay: 0.4s;
        }
      `}</style>

      {/* Animated Shield Icon Box */}
      <div
        className="shield-icon w-16 h-16 rounded-[16px] flex items-center justify-center mb-6 cursor-pointer"
        style={{
          background: "linear-gradient(145deg,#1E3A5F,#0F2A47)",
          boxShadow: "0 4px 20px rgba(15,42,71,0.25)",
        }}
      >
        <ShieldIcon />
      </div>

      {/* Animated Title */}
      {/* animate-on-scroll text-[clamp(30px,5vw,58px)] font-extrabold text-[#0D1B2A] tracking-[-0.03em] leading-[1.1] text-center mb-3.5 */}
      <h2 className="ProtectWorkSection_Heading">How we protect your work</h2>

      {/* Animated Subtitle */}
      {/* animate-on-scroll text-[16px] text-gray-500 text-center mb-14 max-w-2xl */}
      <p className="ProtectWorkSection_Para">
        Security designed for real teams with real work
      </p>

      {/* Animated Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full max-w-[1060px]">
        {cards.map(({ icon, bg, titleColor, title, desc }, index) => (
          <div
            key={title}
            // className={`card animate-on-scroll stagger-${index + 1} rounded-[20px] p-8 sm:p-10 flex flex-col gap-4
            //  min-h-[220px] cursor-pointer group`}
            className={`ProtectWorkSection_Card card animate-on-scroll stagger-${index + 1}`}
            style={{ background: bg }}
          >
            {/* Animated Icon Box */}
            <div className="card-icon w-[52px] h-[52px] bg-white rounded-[14px] flex items-center justify-center shadow-[0_1px_6px_rgba(0,0,0,0.06)]">
              {icon}
            </div>

            {/* Text Content */}
            <div>
              <h3
                className={`card-title ProtectWorkSection_title
                   ${titleColor}`}
              >
                {title}
              </h3>
              <p className="ProtectWorkSection_desc">{desc}</p>
            </div>

            {/* Decorative hover line */}
            {/* <div className="card-underline" /> */}
          </div>
        ))}
      </div>

      {/* Floating Background Elements */}
      {/* <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="float-bg w-32 h-32 top-10 left-10 delay-1" />
        <div className="float-bg w-48 h-48 bottom-20 right-10 delay-2" />
        <div className="float-bg w-24 h-24 top-1/2 left-1/4 delay-3" />
        <div className="float-bg w-40 h-40 bottom-1/3 right-1/4 delay-4" />
        <div className="float-bg w-56 h-56 top-20 right-20 delay-5" />
      </div> */}
    </section>
  );
}
